/**
 * 事件处理录屏管理器
 *
 * 封装 MediaRecorder 屏幕采集 + OSS PostObject 直传
 *
 * 依赖：后端 GET /api/oss/sts 返回 PostObject 所需参数
 *   {
 *     host: 'https://bucket.oss-cn-shenzhen.aliyuncs.com',
 *     key: 'park/xxx/2026-06-10/xxx/Video/xxx.webm',
 *     accessKeyId, policy, signature, securityToken
 *   }
 *
 * 流程（整班只弹一次框）：
 *   1. acquireScreen()  整班首次调用 getDisplayMedia 采集屏幕，之后事件复用同一路 stream
 *   2. start()          幂等确保采集会话存在 + 拉 STS + 在 stream 上新建并启动 MediaRecorder
 *   3. stopAndUpload()  停止本次 MediaRecorder + PostObject 上传，返回公网 URL（保留 stream 复用）
 *   4. cancel()         仅停止本次录制（保留会话）；endSession()/cleanup() 才彻底关闭共享
 *
 * 注意（已知 MVP 限制）：
 *   - getDisplayMedia 采集的是系统声音 + 屏幕画面，**无法**采集 jssip 对讲音频
 *   - 对讲音频需通过 Web Audio API createMediaStreamDestination 注入（V2 实现）
 */

import Vue from 'vue'
import api from '@/api/api'

class ScreenRecorder {
  constructor() {
    this.mediaRecorder = null
    this.chunks = []
    this.stream = null
    this.started = false        // 当前事件是否正在录制
    this.sessionActive = false  // 整班屏幕采集会话是否进行中（stream 存活，避免重复弹框）
    this.needRestart = false    // 采集失败（用户拒绝等）后，是否需要展示"重新启动录屏"按钮
    // 启动时已拉好的 STS 参数，key 在录制期间固定
    this.stsParams = null
    // Web Audio API 音频混合器（将对讲音频注入录屏 stream，不依赖系统音频采集）
    this._audioCtx = null
    this._audioDestination = null
    this._intercomSource = null
  }

  /**
   * 启动录屏（单个事件）
   * 设计：屏幕采集（getDisplayMedia）整班只弹一次框；之后每个事件复用同一路 stream，
   *       只新建/启停 MediaRecorder，不再重复弹框。
   * @param {string} parkName 车场名（拼 OSS 路径用）
   * @param {string} gateName 通道名（拼 OSS 路径用）
   * @returns {Promise<{ok: boolean, reason?: string}>}
   */
  async start(parkName, gateName) {
    if (this.started) {
      console.warn('[ScreenRecorder] 已在运行中，忽略重复 start')
      return { ok: true }
    }

    // 0. 确保屏幕采集会话存在（首次会弹框，之后的事件复用不再弹）
    const acq = await this.acquireScreen()
    if (!acq.ok) {
      return acq
    }

    // 1. 拉 STS 凭证（key 每个事件重新生成，避免上传时再生成路径）
    try {
      const stsResp = await api.getOssSts({ parkName, gateName })
      if (!stsResp || !stsResp.data || !stsResp.data.key) {
        throw new Error('STS 返回缺少 key 字段')
      }
      this.stsParams = stsResp.data
    } catch (e) {
      console.error('[ScreenRecorder] 拉 STS 失败:', e)
      this.needRestart = true
      screenRecorderState.needRestart = true
      return { ok: false, reason: 'OSS 凭证获取失败' }
    }

    // 2. 在已存在的 stream 上创建 MediaRecorder（优先 VP9，压缩率比 VP8 高 ~30%）
    const codecs = [
      'video/webm;codecs=vp9,opus',
      'video/webm;codecs=vp8,opus',
      'video/webm'
    ]
    const mimeType = codecs.find(c => MediaRecorder.isTypeSupported(c)) || 'video/webm'
    try {
      this.mediaRecorder = new MediaRecorder(this.stream, {
        mimeType,
        videoBitsPerSecond: 1000000,  // 1Mbps
        audioBitsPerSecond: 64000
      })
    } catch (e) {
      console.error('[ScreenRecorder] MediaRecorder 创建失败:', e)
      this.needRestart = true
      screenRecorderState.needRestart = true
      this._resetEvent()
      return { ok: false, reason: '浏览器不支持 MediaRecorder' }
    }

    this.chunks = []
    this.mediaRecorder.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) {
        this.chunks.push(e.data)
      }
    }

    // 录制中途错误（内存不足、编码器崩溃等）→ 自动停止并标记可重试
    this.mediaRecorder.onerror = (e) => {
      console.error('[ScreenRecorder] MediaRecorder 录制错误:', e.error || e)
      this._resetEvent()
      this.needRestart = true
      screenRecorderState.needRestart = true
    }

    this.mediaRecorder.start(1000)  // 每秒一个 chunk
    this.started = true
    this.needRestart = false
    screenRecorderState.recording = true
    screenRecorderState.needRestart = false
    console.log(`[ScreenRecorder] 录制开始 (codec: ${mimeType}): ${this.stsParams.key}`)
    return { ok: true }
  }

  /**
   * 屏幕采集（整班只调用一次）
   * 幂等：若已有存活的 stream 会话，直接复用，不重复弹框。
   * 注意：getDisplayMedia 的选择器与"共享系统音频"复选框仍由浏览器强制要求用户手动操作，
   *       任何 Web API 都无法跳过或默认勾选（浏览器安全限制）。
   * @returns {Promise<{ok: boolean, reason?: string}>}
   */
  async acquireScreen() {
    // 已有存活会话则直接复用
    if (this.sessionActive && this.stream && this.stream.active) {
      return { ok: true }
    }

    try {
      this.stream = await navigator.mediaDevices.getDisplayMedia({
        video: {
          frameRate: 10,
          width: { ideal: 1280 },
          height: { ideal: 720 },
          // 预选"整个屏幕"（Chrome/Edge 会隐藏当前标签页，录当前页面只能选整个屏幕）
          displaySurface: 'monitor'
        },
        audio: true
      })
    } catch (e) {
      console.error('[ScreenRecorder] 屏幕采集失败:', e)
      this._endSessionStream()
      this.needRestart = true   // 标记需要展示"重新启动录屏"按钮
      screenRecorderState.needRestart = true
      return { ok: false, reason: '用户拒绝屏幕共享或浏览器不支持' }
    }
    
    // 检测用户是否勾选了"共享系统音频"（Chrome/Edge 行为一致）
    // 未勾选时 stream 没有音频轨道，录出来的视频会无声
    if (this.stream.getAudioTracks().length === 0) {
      console.warn('[ScreenRecorder] 未检测到音频轨道，用户可能未勾选"共享系统音频"')
      // 停止已采集的视频轨道，释放资源
      this.stream.getTracks().forEach(t => { try { t.stop() } catch (e) { /* ignore */ } })
      this.stream = null
      this.needRestart = true
      screenRecorderState.needRestart = true
      return { ok: false, reason: '未勾选【共享系统音频】，录屏将无声。请重新点击录屏并勾选' }
    }

    // ========== Web Audio API 音频混合器 ==========
    // 将对讲音频直接注入录屏 stream，不依赖系统音频采集（某些 Windows 音频驱动不可靠）
    this._setupAudioMixer()

    // 用户点击浏览器自带的"停止共享"按钮 → 整班会话结束，下次需重新采集
    const videoTrack = this.stream.getVideoTracks()[0]
    if (videoTrack) {
      videoTrack.onended = () => {
        console.log('[ScreenRecorder] 用户主动停止共享，结束屏幕采集会话')
        this._endSessionStream()
      }
    }

    this.sessionActive = true
    return { ok: true }
  }

  /**
   * 停止 + 上传，返回 OSS 公网 URL
   * 注意：仅结束当前事件录制，保留整班屏幕采集会话（stream 不关闭），
   *       下一个事件直接 start() 复用，不再弹框。
   * @returns {Promise<string|null>} 成功返回 URL，失败返回 null
   */
  async stopAndUpload() {
    if (!this.started || !this.mediaRecorder) {
      console.warn('[ScreenRecorder] 未启动，跳过 stopAndUpload')
      return null
    }
    if (!this.stsParams) {
      console.error('[ScreenRecorder] 缺少 STS 参数，无法上传')
      this._resetEvent()
      return null
    }

    try {
      const blob = await this._stopMediaRecorder()
      const url = await this._uploadToOss(blob)
      this._resetEvent()
      return url
    } catch (e) {
      console.error('[ScreenRecorder] stopAndUpload 失败:', e)
      this._resetEvent()
      return null
    }
  }

  /**
   * 取消当前事件录屏（仅停止录制、不关闭整班屏幕采集会话，便于下一个事件复用）
   * 用于关闭兜底、放弃本轮录制等场景。
   * 注意：页面销毁（beforeDestroy）应调用 endSession() 彻底关闭共享。
   */
  cancel() {
    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      try {
        this.mediaRecorder.stop()
      } catch (e) {
        // 忽略重复 stop
      }
    }
    this._resetEvent()
  }

  /**
   * 结束整班屏幕采集会话（停止并释放 stream，红色"正在共享"条消失）
   * 用于坐席登出 / 页面销毁。之后再次 start() 会重新弹框采集。
   */
  endSession() {
    this._endSessionStream()
  }

  // ========== 私有方法 ==========

  /**
   * 停止 MediaRecorder 并产出 blob（不关闭 stream，便于会话复用）
   */
  _stopMediaRecorder() {
    return new Promise((resolve) => {
      this.mediaRecorder.onstop = () => {
        const blob = new Blob(this.chunks, { type: 'video/webm' })
        resolve(blob)
      }
      this.mediaRecorder.stop()
    })
  }

  /**
   * 初始化 Web Audio API 音频混合器
   * 把屏幕采集的系统音频 + 对讲音频混合后输出到同一个 audio track，
   * 这样 MediaRecorder 就能同时录到两者，不再依赖 Windows 系统音频采集的可靠性。
   */
  _setupAudioMixer() {
    try {
      this._audioCtx = new (window.AudioContext || window.webkitAudioContext)()
      this._audioDestination = this._audioCtx.createMediaStreamDestination()

      // 把 getDisplayMedia 采集到的系统音频路由到混合器
      const systemAudioTracks = this.stream.getAudioTracks()
      if (systemAudioTracks.length > 0) {
        const systemSource = this._audioCtx.createMediaStreamSource(
          new MediaStream([systemAudioTracks[0]])
        )
        systemSource.connect(this._audioDestination)
      }

      // 用混合器输出的 audio track 替换原始 audio track
      const mixedAudioTrack = this._audioDestination.stream.getAudioTracks()[0]
      systemAudioTracks.forEach(t => this.stream.removeTrack(t))
      this.stream.addTrack(mixedAudioTrack)

      console.log('[ScreenRecorder] 音频混合器已初始化，系统音频已路由')
    } catch (e) {
      console.error('[ScreenRecorder] 音频混合器初始化失败，回退到原始音频:', e)
      this._audioCtx = null
      this._audioDestination = null
    }
  }

  /**
   * 注入对讲音频到录屏 stream（供对讲组件在 ontrack 时调用）
   * 通过对讲组件拿到的远端 MediaStream 直接路由到混合器，
   * 确保对讲声音被 MediaRecorder 录到。
   * @param {MediaStream} intercomStream 对讲远端音频流
   */
  addIntercomAudio(intercomStream) {
    if (!this._audioCtx || !this._audioDestination) {
      console.warn('[ScreenRecorder] 音频混合器未初始化，跳过对讲音频注入')
      return
    }
    // 清理旧的对讲音频源
    if (this._intercomSource) {
      try { this._intercomSource.disconnect() } catch (e) { /* ignore */ }
    }
    try {
      this._intercomSource = this._audioCtx.createMediaStreamSource(intercomStream)
      this._intercomSource.connect(this._audioDestination)
      console.log('[ScreenRecorder] 对讲音频已注入录屏 stream')
    } catch (e) {
      console.error('[ScreenRecorder] 对讲音频注入失败:', e)
    }
  }

  /**
   * 重置单个事件的状态（保留 stream / sessionActive，供下一事件复用）
   */
  _resetEvent() {
    this.mediaRecorder = null
    this.chunks = []
    this.started = false
    this.stsParams = null
    screenRecorderState.recording = false
  }

  /**
   * 彻底结束整班屏幕采集会话：停止所有 track 并清空全部状态
   */
  _endSessionStream() {
    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      try { this.mediaRecorder.stop() } catch (e) { /* ignore */ }
    }
    // 释放音频混合器资源
    if (this._intercomSource) {
      try { this._intercomSource.disconnect() } catch (e) { /* ignore */ }
      this._intercomSource = null
    }
    if (this._audioCtx) {
      try { this._audioCtx.close() } catch (e) { /* ignore */ }
      this._audioCtx = null
      this._audioDestination = null
    }
    if (this.stream) {
      this.stream.getTracks().forEach(t => {
        try { t.stop() } catch (e) { /* ignore */ }
      })
    }
    this.stream = null
    this.sessionActive = false
    this.needRestart = false
    screenRecorderState.needRestart = false
    screenRecorderState.recording = false
    this._resetEvent()
  }

  _uploadToOss(blob) {
    const { host: rawHost, key, accessKeyId, policy, signature, securityToken } = this.stsParams
    // 前端在用户浏览器运行，无法访问 OSS 内网地址，去掉 -internal
    const host = rawHost.replace('-internal', '')
    const form = new FormData()
    form.append('key', key)
    form.append('OSSAccessKeyId', accessKeyId)
    form.append('policy', policy)
    form.append('signature', signature)
    form.append('x-oss-security-token', securityToken)
    form.append('success_action_status', '200')
    form.append('file', blob, key.split('/').pop())

    // 用原生 fetch 避免 axios 自动设置 Content-Type 干扰 multipart
    return fetch(host, {
      method: 'POST',
      body: form
    }).then(res => {
      if (!res.ok) {
        throw new Error(`OSS HTTP ${res.status}`)
      }
      return `${host}/${key}`
    })
  }

  // 兼容保留：彻底结束整班会话（等价于 endSession）
  cleanup() {
    this._endSessionStream()
  }

  isRecording() {
    return this.started
  }
}

// 响应式 UI 状态（Vue 2 中模块单例的普通属性不是响应式的，必须用 Vue.observable 包裹，
// 否则“重新启动录屏”按钮的显隐不会随状态变化而重渲染）。
export const screenRecorderState = Vue.observable({
  needRestart: false, // 采集失败（用户拒绝等）后需要展示重试按钮
  recording: false    // 当前是否正在录制
})

const recorder = new ScreenRecorder()
// 挂载到实例上，模板用 screenRecorder.uiState 读取（保持单例引用不变，外部调用方无需改 import）
recorder.uiState = screenRecorderState

export default recorder



// WEBPACK FOOTER //
// ./src/utils/screenRecorder.js